/**
 * TraitService -- pure-function queries over character traits.
 * Resolves mechanical effects from CharacterTrait[] for use by other services.
 */

import type { CharacterTrait } from '../types/traits';
import type { Modifier } from '../types/dice';
import { ModifierSource } from '../types/dice';

export interface NaturalWeapon {
  name: string;
  damage: string;
  type: 'R' | 'I' | 'E';
  pen: number;
  qualities?: string[];
  sourceTraitId: string;
}

export class TraitService {
  /** Get unnatural characteristic multipliers. Highest wins per characteristic. */
  static getUnnaturalCharacteristics(traits: CharacterTrait[]): Map<string, number> {
    const result = new Map<string, number>();
    for (const t of traits) {
      const uc = t.mechanics?.unnaturalCharacteristic;
      if (!uc) continue;
      const multiplier = t.parameters?.multiplier ?? uc.multiplier;
      const current = result.get(uc.characteristic) ?? 0;
      if (multiplier > current) result.set(uc.characteristic, multiplier);
    }
    return result;
  }

  /**
   * Multiplier applied to one characteristic's bonus, or 1 when no Unnatural
   * trait covers it.
   *
   * RT Core, Unnatural Characteristic: "select a Characteristic, and double its
   * bonus … one selection multiplies the Characteristic Bonus by X2, two
   * selections by x3".
   */
  static getUnnaturalMultiplier(
    traits: CharacterTrait[] | undefined,
    characteristic: string
  ): number {
    if (!traits || traits.length === 0) return 1;
    return this.getUnnaturalCharacteristics(traits).get(characteristic) ?? 1;
  }

  /**
   * Multiplier for one characteristic read from an NPC's free-text trait list.
   *
   * NPC stat blocks store traits as prose strings ("Unnatural Strength (x2)")
   * rather than structured records, so the structured resolver above cannot see
   * them. A bare "Unnatural Strength" counts as x2, matching the catalogue
   * record's own name. Traits that merely begin with "Unnatural" but name no
   * characteristic — Unnatural Senses, Unnatural Speed — are ignored.
   */
  static getUnnaturalMultiplierFromNames(
    traitNames: string[] | undefined,
    characteristic: string
  ): number {
    let best = 1;
    for (const name of traitNames ?? []) {
      const match = /^Unnatural\s+([A-Za-z]+)\b(?:\s*\(\s*x\s*(\d+)\s*\))?/i.exec(name);
      if (!match) continue;
      if (match[1].toLowerCase() !== characteristic.toLowerCase()) continue;
      const multiplier = match[2] ? Number(match[2]) : 2;
      if (multiplier > best) best = multiplier;
    }
    return best;
  }

  /** Total bonus AP from Machine/Natural Armour traits. Highest wins (not additive). */
  static getTraitArmour(traits: CharacterTrait[]): number {
    let max = 0;
    for (const t of traits) {
      const ap = t.parameters?.rating ?? t.mechanics?.armourBonus ?? 0;
      if (ap > max) max = ap;
    }
    return max;
  }

  /** All immunities from traits (union of all). */
  static getImmunities(traits: CharacterTrait[]): Set<string> {
    const result = new Set<string>();
    for (const t of traits) {
      t.mechanics?.immunity?.forEach((i) => result.add(i));
    }
    return result;
  }

  /** Check if any trait has a specific rule override. */
  static hasRuleOverride(traits: CharacterTrait[], rule: string): boolean {
    return traits.some((t) => t.mechanics?.ruleOverride === rule);
  }

  /** Get natural weapons as weapon-like objects for combat. */
  static getNaturalWeapons(traits: CharacterTrait[]): NaturalWeapon[] {
    return traits
      .filter((t) => t.mechanics?.naturalWeapon)
      .map((t) => ({ ...t.mechanics!.naturalWeapon!, sourceTraitId: t.id }));
  }

  /** Get trait-sourced modifiers for the calculation pipeline. */
  static getTraitModifiers(traits: CharacterTrait[]): Modifier[] {
    const mods: Modifier[] = [];
    for (const t of traits) {
      if (!t.mechanics?.modifiers) continue;
      for (const m of t.mechanics.modifiers) {
        mods.push({
          name: t.name,
          value: m.value,
          source: ModifierSource.Trait,
          description: m.condition ? `${t.name}: ${m.condition}` : t.name
        });
      }
    }
    return mods;
  }
}
